import { defineStore } from 'pinia';
import API from "@/shared/helpers/API";
import {useUserStore, useRoomsStore, useMenuStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";

export const useFunnelStore = defineStore('funnel', {
	state: () => ({
		STEP_MENU: 'menu',
		STEP_ROOMS: 'rooms',
		STEP_SUBSCRIPTION: 'subscription',
		STEP_STRIPE: 'stripe',
		STEP_VIDEO: 'video',
		STEP_COMPLETED: 'completed',
		// Funnel-choice popup selections, persisted in localStorage as funnel-choice-<businessId>
		CHOICE_MOBILE: 'mobile',
		CHOICE_COUNTER: 'counter',
		CHOICE_COMPLETE: 'complete',
		canTransition: false,
		loadingFunnelData: false,
		wentToShop: false,
		updatingProgress: false,
		pollingTimeout: null,
		pollingIterations: 0,
		fetchingBusiness: false,
		pollingMenuUpload: false,
		errorMenuUpload: false,
		completedMenuUpload: Number(localStorage.getItem("menu-upload-complete") || 0) || null,
		aiProgress: 0,
		aiUploadBusinessId: null,
		showEventsPhysicalManual: false,
		funnelChoice: null,
	}),
	getters: {
		//PROGRESS_STEPS: ['menu', 'rooms', 'subscription', 'stripe', 'video', 'completed'],
		PROGRESS_STEPS: state => [state.STEP_MENU, state.STEP_ROOMS, state.STEP_SUBSCRIPTION, state.STEP_STRIPE, state.STEP_VIDEO, state.STEP_COMPLETED],
		userStore: state => useUserStore(),
		menuStore: () => useMenuStore(),
		roomsStore: () => useRoomsStore(),
		currBusiness: () => useUserStore()?.currBusiness,
		getString: () => useLanguageStore()?.getString,
		counterFunnelSteps: state => {
			return [
				{...state.registryStep, trackEventId: 'step1_restaurant'},
				{
					title: state.getString('rooms_creation'),
					trackEventId: 'step2_restaurant',
					completed: state.completedRoomCreation,
					slotName: 'room-creation',
					video: {
						id: 'fhMkgiU1kJY',
						title: state.getString('funnel_video_rooms_title'),
						description: state.getString('funnel_video_rooms_description'),
					}
				},
				state.goLiveStep,
				state.stripeLinkStep,
			]
		},
		eventsFunnelSteps: state => {
			return [
				{...state.registryStep, trackEventId: 'step1_events'},
				{
					title: state.getString('link_creation', [state.userStore?.menuWord]),
					trackEventId: 'step2_events',
					completed: state.completedEventsRoomCreation,
					slotName: 'link-creation',
					video: {
						id: 'fhMkgiU1kJY',
						title: state.getString('funnel_video_rooms_title'),
						description: state.getString('funnel_video_rooms_description'),
					}
				},
				state.goLiveStep,
				state.stripeLinkStep,
				state.eventsVideoStep,
			];
		},
		retailFunnelSteps: state => {
			return [
				{...state.registryStep, trackEventId: 'step1_other'},
				state.goLiveStep,
				state.stripeLinkStep
			]
		},
		mobileFunnelSteps: state => {
			return [
				state.goLiveStep,
				state.stripeLinkStep,
			]
		},
		counterChoiceFunnelSteps: state => {
			return [
				{...state.registryStep, trackEventId: 'step1_restaurant'},
				{
					title: state.getString('rooms_creation'),
					trackEventId: 'step2_restaurant',
					completed: state.completedRoomCreation,
					slotName: 'room-creation',
					video: {
						id: 'fhMkgiU1kJY',
						title: state.getString('funnel_video_rooms_title'),
						description: state.getString('funnel_video_rooms_description'),
					}
				},
				state.goLiveStep,
				state.stripeLinkStep,
			]
		},
		completeFunnelSteps: state => {
			return [
				{...state.registryStep, trackEventId: 'step1_restaurant'},
				{
					title: state.getString('rooms_creation'),
					trackEventId: 'step2_restaurant',
					completed: state.completedRoomCreation,
					slotName: 'room-creation',
					video: {
						id: 'fhMkgiU1kJY',
						title: state.getString('funnel_video_rooms_title'),
						description: state.getString('funnel_video_rooms_description'),
					}
				},
				state.goLiveStep,
				state.stripeLinkStep,
			]
		},
		registryStep: state => {
			let registryStepTitle = state.getString('products_registry');
			if(state.pollingMenuUpload) {
				registryStepTitle = (state.aiProgress || 0) > 1 ? state.getString('generating_images') : state.getString('menu_loading', [state.userStore.menuWord]);
			} else if(state.completedMenuUpload === Number(state.userStore.currBusinessId) && !state.errorMenuUpload && !state.pollingMenuUpload) {
				registryStepTitle = state.getString('menu_loaded', [state.userStore.menuWord]);
			}
			return {
				title: registryStepTitle,
				trackEventId: null,
				slotName: 'products-registry',
				completed: state.completedProductsRegistry,
				video: {
				id: 'NWXP7zFyPf4',
					title: state.getString('funnel_video_registry_title'),
					description: state.getString('funnel_video_registry_description'),
				}
			}
		},
		goLiveStep: state => {
			let trialAvailable = state.userStore.user && state.userStore.user.stripe_trial !== 1 && state.userStore.currBusiness && !state.userStore.currBusiness.stripe_trial;
			let finalTitle = 'go_live_free_trial';
			if(!trialAvailable) {
				finalTitle = state.userStore?.currBusiness?.type === 'events' ? 'title_page_prices_events' : 'title_page_prices';
			}
			return {
				title: state.getString(finalTitle),
				completed: state.completedGoLiveCounter,
				slotName: 'go-live',
			}
		},
		stripeLinkStep: state => {
			return {
				title: state.getString('funnel_stripe_step_title'),
				completed: state.completedStripeLink,
				slotName: 'receive-payments',
				video: {
					id: 'N3JRhr6nAx4',
					title: state.getString('funnel_video_stripe_title'),
					description: state.getString('funnel_video_stripe_description'),
				}
			}
		},
		eventsVideoStep: state => {
			return {
				title: state.getString('funnel_events_user_manual'),
				completed: state.savedProgress >= state.getStepIndex(state.STEP_VIDEO),
				slotName: 'events-video',
			}
		},
		funnelsMap: state => {
			return {
				restaurant: {
					bannerData: {
						src: 'https://qromomilan.s3.eu-south-1.amazonaws.com/img/funnel/gestionale.png',
						title: state.getString('funnel_counter_banner_title'),
						text: state.getString('funnel_counter_banner_text'),
					},
					steps: state.counterFunnelSteps
				},
				events: {
					bannerData: {
						src: 'https://img.qromo.io/img/web-site/new/order-pay-navabr.png',
						//title: state.getString('funnel_banner_title', [state.getString('business_package_skip_cashier')]),
						title: state.getString('funnel_counter_banner_title'),
						text: state.getString('funnel_counter_banner_text'),
					},
					steps: state.eventsFunnelSteps
				},
				other: {
					bannerData: {
						src: 'https://qromomilan.s3.eu-south-1.amazonaws.com/img/funnel/gestionale.png',
						//title: state.getString('funnel_banner_title', [state.getString('business_package_counter')]),
						title: state.getString('funnel_counter_banner_title'),
						text: state.getString('funnel_counter_banner_text'),
					},
					steps: state.retailFunnelSteps
				},
				// Funnel-choice popup funnels, keyed by the selected card id.
				[state.CHOICE_MOBILE]: {
					bannerData: {
						src: 'https://img.qromo.io/img/funnel/banner-new-vendi-in-mobilita.jpg',
						title: state.getString('funnel_counter_banner_title'),
						text: state.getString('funnel_counter_banner_text'),
					},
					steps: state.mobileFunnelSteps
				},
				[state.CHOICE_COUNTER]: {
					bannerData: {
						src: 'https://img.qromo.io/img/funnel/banner-new-vendita-banco.jpg',
						title: state.getString('funnel_counter_banner_title'),
						text: state.getString('funnel_counter_banner_text'),
					},
					steps: state.counterChoiceFunnelSteps
				},
				[state.CHOICE_COMPLETE]: {
					bannerData: {
						src: 'https://img.qromo.io/img/funnel/banner-new-gestione-completa.jpg',
						title: state.getString('funnel_counter_banner_title'),
						text: state.getString('funnel_counter_banner_text'),
					},
					steps: state.completeFunnelSteps
				},
			}
		},
		menuItems: state => state.menuStore?.items,
		ready: state => !state.loadingFunnelData && !!state?.menuStore?.loadedAndNormalizedMenu && !!state.currBusiness,
		getStepIndex: state => (stepName) => {
			if(!stepName) { return -1; }
			return state.PROGRESS_STEPS.findIndex(i => i === stepName);
		},
		savedProgress: state => {
			if(!state.currBusiness || !state.currBusiness?.funnel_progress) { return -1; }
			return state.getStepIndex(state.currBusiness?.funnel_progress);
		},
		greaterThanSavedProgress: state => (stepName) => {
			if(!state.currBusiness || !stepName) { return false; }
			return state.savedProgress < state.getStepIndex(stepName);
		},
		lessThanSavedProgress: state => (stepName) => {
			if(!state.currBusiness || !stepName) { return false; }
			return state.savedProgress > state.getStepIndex(stepName);
		},
		hasActiveFunnel: state => {
			if(!state.userStore?.currPermissions || !state.userStore.currPermissions.includes('admin')) {
				return false;
			}
			if(!state.ready) {
				return !!state?.currFunnel?.steps && state.savedProgress >= 0 && state.savedProgress < state.PROGRESS_STEPS.length - 1;
			}
			return state.stepperProgress !== 0
				&& state.currBusiness?.funnel_progress !== state.STEP_COMPLETED
				&& state?.currFunnel?.steps
				&& state?.currFunnel?.steps?.length
		},
		showStepsBottomSheet: state => {
			if(!state.userStore?.currPermissions) {
				return false;
			}
			if(!state.ready) {
				return state.hasActiveFunnel && state.savedProgress >= 0 && state.greaterThanSavedProgress(state.STEP_COMPLETED);
			}
			return state.hasActiveFunnel
				&& state?.currFunnel?.steps?.length >= state.stepperProgress;
		},
		showFunnelChoicePopup: state => {
			if(!state.userStore?.currPermissions) {
				return false;
			}
			return !!state.userStore.currPermissions?.includes('admin')
				&& state.savedProgress === -1            // funnel_progress NULL => brand new business
				&& state.currBusiness?.country === 'IT';  // funnel is IT-only
		},
		showEndedFunnelPopup: state => {
			if(!state.userStore?.currPermissions || !state.currBusiness || !state.currBusiness?.funnel_progress || state.currBusiness?.country !== 'IT') {
				return false;
			}
			if(!state.ready) {
				let finalStepBeforeCompleted = state.currBusiness.type === 'events' ? state.STEP_VIDEO : state.STEP_STRIPE;
				return state.savedProgress === state.getStepIndex(finalStepBeforeCompleted);
			}
			return state.hasActiveFunnel
				&& state?.currFunnel?.steps?.length + 1 === state.stepperProgress
				&& state.currBusiness?.funnel_progress !== state.STEP_COMPLETED;
		},
		selectedFunnelChoice: state => {
			if([state.CHOICE_MOBILE, state.CHOICE_COUNTER, state.CHOICE_COMPLETE].includes(state.funnelChoice)) {
				return state.funnelChoice;
			}
			return null;
		},
		currFunnel: state => {
			if(!state.ready || !state.currBusiness?.type || state.currBusiness?.country !== 'IT') {
				return null;
			}
			if(state.selectedFunnelChoice && state.funnelsMap[state.selectedFunnelChoice]) {
				return state.funnelsMap[state.selectedFunnelChoice];
			}
			return state.funnelsMap[state.currBusiness.type] || null;
		},
		completedProductsRegistry: state => {
			if(!state.ready) {
				return state.savedProgress >= state.getStepIndex(state.STEP_MENU);
			}
			if((state.pollingMenuUpload && (state.aiProgress || 0) > 0) || state.errorMenuUpload) {
				return true;
			}
			if(!state.menuStore?.items || !Object.keys(state.menuStore.items)?.length) {
				return false;
			}
			return Object.keys(state.menuStore.items).length >= 3;
		},
		completedRoomCreation: state => {
			if(!state.ready) {
				return state.savedProgress >= state.getStepIndex(state.STEP_ROOMS);
			}
			if(!state.roomsStore?.rooms  || !state.roomsStore.rooms?.length) {
				return false;
			}
			return state.roomsStore.rooms.length > 0;
		},
		completedEventsRoomCreation: state => {
			if(!state.ready) {
				return state.savedProgress >= state.getStepIndex(state.STEP_ROOMS);
			}
			if(!state.roomsStore?.rooms  || !state.roomsStore.rooms?.length) {
				return false;
			}
			return state.userStore.currBusiness?.link && state.roomsStore.rooms.length > 0 && state.roomsStore.rooms.some(room => !!room.type);
		},
		completedGoLiveCounter: state => {
			if(!state.ready) {
				return state.savedProgress >= state.getStepIndex(state.STEP_SUBSCRIPTION);
			}
			return !!state.currBusiness.plan_id && state.currBusiness.plan_plugin_counter;
		},
		completedStripeLink: state => {
			if(!state.ready) {
				return state.savedProgress >= state.getStepIndex(state.STEP_STRIPE);
			}
			return !!state.currBusiness.stripe_account && !!state.currBusiness.config_payments;
		},
		stepperProgress: state => {
			if(!state.ready || !state?.currFunnel?.steps) {
				return 0;
			}
			let progress = 1;
			let steps = state.currFunnel.steps;
			for (let i = 0; i < steps.length; i++) {
				if(steps[i].completed) {
					progress++;
				} else {
					break;
				}
			}
			return progress;
		},
		currStep: state => {
			if(!state.ready || !state?.currFunnel?.steps?.length || !state.stepperProgress || !state?.currFunnel?.steps?.[state.stepperProgress - 1]) {
				return null;
			}
			return state.currFunnel.steps[state.stepperProgress - 1];
		},
	},
	actions: {
		loadFunnelChoice() {
			let businessId = this.userStore?.currBusinessId;
			this.funnelChoice = businessId ? localStorage.getItem('funnel-choice-' + businessId) : null;
		},
		setFunnelChoice(choice) {
			let businessId = this.userStore?.currBusinessId;
			if(!businessId) { return; }
			localStorage.setItem('funnel-choice-' + businessId, choice);
			this.funnelChoice = choice;
		},
		init(callback = null) {
			this.loadFunnelChoice();
			if(this.loadingFunnelData || !this.currBusiness) {
				return;
			}
			let finalFn = () => {
				if(this.menuStore.loadedAndNormalizedMenu && this.roomsStore.roomsTables) {
					this.loadingFunnelData = false;
					if(callback) {
						callback();
					}
				}
			}
			if(this.menuStore.loadedAndNormalizedMenu && this.roomsStore.roomsTables) {
				if(callback) { callback(); }
			} else {
				this.loadingFunnelData = true;
				this.menuStore.loadMenu(finalFn);
				this.roomsStore.getBusinessRooms(finalFn);
			}
		},
		startPollingForStripeLink() {
			if(this.fetchingBusiness) {
				return;
			}
			let check = ()=>{
				clearTimeout(this.pollingTimeout);
				if(!this.completedStripeLink || this.pollingIterations <= 20) {
					this.pollingIterations++;
					this.fetchingBusiness = true;
					this.userStore.refreshBusiness(this.userStore.currBusinessId, ()=>{
						this.fetchingBusiness = false;
						if(this.completedStripeLink) {
							this.pollingIterations = 0;
						} else {
							this.pollingTimeout = setTimeout(check, 10000);
						}
					});
				}
			}
			check();
		},
		updateBusinessFunnelProgress(newStep, callback = null) {
			if(!newStep || !this.userStore?.currBusinessId || !this.currBusiness || this.loadingFunnelData || this.updatingProgress) {
				if(callback) { callback(false); }
				return;
			}
			if (this.getStepIndex(newStep) < this.savedProgress) { if (callback) callback(false); return; }
			this.updatingProgress = true;
			API.init().updateBusinessFunnelProgress(this.userStore.currBusinessId, newStep, response => {
				this.updatingProgress = false;
				if (response && response.result === "OK") {
					this.currBusiness.funnel_progress = newStep;
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false); }
				}
			});
		},
	}
});